<template>
  <Teleport to="[data-header-action]" v-if="isMounted">
    <ButtonComponent
      outlined
      label="New script"
      @click="showingCreateScriptModal = true"
    >
      <template #icon><JBIcon name="add" /></template>
    </ButtonComponent>
  </Teleport>

  <section class="[ w-full max-w-3xl mx-auto py-6 ]">
    <div v-if="loadingState === ILoadingState.LOADING">
      <JBLoadingSpinner message="Fetching scripts" />
    </div>
    <div v-else-if="loadingState === ILoadingState.ERROR">
      There was an error fetching the scripts, please try again
    </div>
    <div v-else class="[ flex flex-col gap-6 ]">
      <template v-if="scripts.length">
        <ScriptCard
          v-for="script in scripts"
          :script="script"
          :key="script.id"
          @remove-script="deleteScript"
          @update-script="updateScript"
        />
      </template>

      <JBEmptyListMessage
        class="[ border rounded ]"
        title="No scripts configured"
        message=""
        icon="hierarchy-square"
        v-else
      >
        <template #buttons>
          <ButtonComponent
            @click="showingCreateScriptModal = true"
            severity="secondary"
          >
            Add a script
          </ButtonComponent>
        </template>
      </JBEmptyListMessage>
    </div>

    <div class="[ mt-8 ]">
      <ButtonComponent
        plain
        label="New Script"
        severity="secondary"
        @click="showingCreateScriptModal = true"
      >
        <template #icon><JBIcon name="add" /></template>
      </ButtonComponent>
    </div>
  </section>

  <JBDialog v-model:visible="showingCreateScriptModal" header="New script">
    <Form
      v-slot="$form"
      :resolver="newScriptFormResolver"
      :initial-values="newScriptForm"
      @submit="createScript"
      @reset="newScriptForm = {}"
    >
      <div class="[ grid grid-cols-[125px_350px] gap-4 ]">
        <label class="[ pt-2 text-sm text-gray-dark ]" for="script-name"
          >Name</label
        >
        <InputText
          v-model="newScriptForm.name"
          name="name"
          id="script-name"
        />
        <div
          class="[ col-start-2 font-bold text-red ]"
          v-if="$form.name?.error"
        >
          {{ $form.name.error.message }}
        </div>

        <label class="[ pt-2 text-sm text-gray-dark ]" for="script-description"
          >Description</label
        >
        <Textarea
          v-model="newScriptForm.description"
          name="description"
          id="script-description"
        />
        <div
          class="[ col-start-2 font-bold text-red ]"
          v-if="$form.description?.error"
        >
          {{ $form.description.error.message }}
        </div>

        <div
          v-if="formState === ILoadingState.ERROR"
          class="[ mt-4 font-bold text-red col-start-2 ]"
        >
          An error occurred, please try again.
        </div>
      </div>

      <div class="[ flex gap-3 justify-end mt-4 ]">
        <ButtonComponent outlined @click="showingCreateScriptModal = false"
          >Cancel</ButtonComponent
        >
        <ButtonComponent
          :disabled="!$form.valid"
          :loading="formState === ILoadingState.LOADING"
          type="submit"
          label="Save"
        />
      </div>
    </Form>
  </JBDialog>
</template>

<script setup lang="ts">
import { Button as ButtonComponent, InputText, Textarea } from "primevue";
import { Form, FormSubmitEvent } from "@primevue/forms";
import JBDialog from "@/components/ui/modals/JBDialog.vue";
import { onMounted, ref } from "vue";
import { storeToRefs } from "pinia";
import { useScriptStore } from "@/store/script.ts";
import { CreateScriptRequest, Script } from "@/services/api/sdk";
import { newScriptFormResolver } from "@/utils/validators/scripts.ts";
import { ILoadingState } from "@/utils/types.ts";
import ScriptCard from "@/components/scripts/ScriptCard.vue";
import JBLoadingSpinner from "@/components/ui/JBLoadingSpinner.vue";
import JBEmptyListMessage from "@/components/ui/JBEmptyListMessage.vue";

definePage({
  meta: {
    title: "Scripts",
    icon: "",
  },
});

const scriptStore = useScriptStore();
const { scripts } = storeToRefs(scriptStore);

const isMounted = ref<boolean>(false);
const loadingState = ref<ILoadingState>(ILoadingState.IDLE);
const formState = ref<ILoadingState>(ILoadingState.IDLE);

const newScriptForm = ref<CreateScriptRequest>({});
const showingCreateScriptModal = defineModel<boolean>();

onMounted(() => {
  isMounted.value = true;
  fetchScripts();
});

async function fetchScripts() {
  try {
    loadingState.value = ILoadingState.LOADING;
    await scriptStore.fetchScripts();
    loadingState.value = ILoadingState.IDLE;
  } catch (error: any) {
    loadingState.value = ILoadingState.ERROR;
  }
}

async function createScript(e: FormSubmitEvent) {
  if (e.valid) {
    try {
      formState.value = ILoadingState.LOADING;
      await scriptStore.createScript(newScriptForm.value);
      await fetchScripts();
      e.reset();
      showingCreateScriptModal.value = false;
      formState.value = ILoadingState.IDLE;
    } catch(e) {
      formState.value = ILoadingState.ERROR;
      console.error("Error creating script", e);
    }
  }
}

async function deleteScript(scriptId: number) {
  await scriptStore.deleteScript(scriptId);
}

async function updateScript(script: Script) {
  await scriptStore.updateScript(script);
}
</script>
